import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import Typography from '@repo/ui/typography'

import CountryFlag from '../../components/elements/country-flag'
import PeriodBadge from '../../components/elements/period-badge'
import { tripTypeMapping } from '../../lib/types/enums/trip/trip-type.enum'
import { TripProperties } from '../../lib/types/models/trip/properties.trip'
import { Trip } from '../../lib/types/models/trip/trip'
import { TripInput } from '../../lib/types/models/trip/trip-input'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'

type TripVersion = 'FULL' | 'PARTIAL' | 'LIGHT' | 'SUMMARY'

export const tripsColumns: () => (Column<Trip> & {
  version: TripVersion[]
})[] = () => [
  {
    version: ['FULL', 'PARTIAL', 'LIGHT'],
    accessorKey: 'type',
    header: 'Type de voyage',
    enableSorting: true,
    cell: ({ row }) => tripTypeMapping[row.original.type].label
  },
  {
    version: ['FULL', 'PARTIAL', 'LIGHT', 'SUMMARY'],
    accessorKey: 'departure.label',
    header: 'Lieu de départ',
    enableSorting: true,
    cell: ({ row }) => TripProperties.getDepartureOrJourneyPlaceLabel(row.original) || '-'
  },
  {
    version: ['FULL', 'PARTIAL', 'LIGHT', 'SUMMARY'],
    accessorKey: 'arrival.label',
    header: `Destination d'arrivée`,
    enableSorting: true,
    cell: ({ row }) => (row.original.arrival ? getTranslatedName(row.original.arrival) : '-')
  },
  {
    version: ['FULL'],
    accessorKey: 'prices',
    header: 'Périodes',
    cell: ({ row }) => {
      return (
        <div className="flex flex-col gap-2 py-2">
          {row.original.prices.map((data, priceIndex) => (
            <Badge
              key={priceIndex}
              color={tailwindColors.white.hover}
              borderColor={tailwindColors.grey.light.DEFAULT}
              className="flex flex-col h-fit gap-1  p-2"
              text={
                <div className="flex flex-col gap-2">
                  <Typography variant="label-medium">
                    Prix : <span className="font-bold">{data.price} €</span>
                  </Typography>
                  <div className="flex gap-1 flex-wrap">
                    {data.periods.map((period, periodIndex) => (
                      <PeriodBadge key={periodIndex} period={period} />
                    ))}
                  </div>
                </div>
              }
            />
          ))}
        </div>
      )
    }
  },
  {
    version: ['FULL', 'PARTIAL', 'SUMMARY'],
    accessorKey: 'transportMeans.name',
    header: 'Moyen de transport',
    enableSorting: true,
    cell: ({ row }) => TripProperties.handleTransportMeansName(row.original.transportMeans)
  },
  {
    version: ['FULL'],
    accessorKey: 'linkedToMainTrip',
    header: 'Voyage lié',
    centerCell: true,
    cell: ({ row }) =>
      valueRender(row.original.linkedToMainTrip, (trip) => TripProperties.getTripLabel(trip))
  },
  {
    version: ['FULL', 'LIGHT'],
    accessorKey: 'countries',
    header: 'Pays de séjour',
    centerCell: true,
    cell: ({ row }) => (
      <div className={'flex items-center gap-2 flex-wrap'}>
        {row.original.countries.map((country) => (
          <CountryFlag code={country.code} key={country.code} />
        ))}
      </div>
    )
  },
  {
    version: ['FULL', 'PARTIAL', 'LIGHT', 'SUMMARY'],
    accessorKey: 'journeyPlace.name',
    header: `Lieu de séjour`,
    centerCell: true,
    enableSorting: true,
    cell: ({ row }) => (row.original.journeyPlace ? row.original.journeyPlace.name : '-')
  },
  {
    version: ['FULL'],
    accessorKey: 'belongsTo',
    header: 'Groupes de voyages',
    cell: ({ row }) => (
      <Typography variant="label-medium" className="whitespace-break-spaces">
        {row.original.belongsTo.length === 0
          ? '-'
          : row.original.belongsTo.map((group) => group.name).join(', ')}
      </Typography>
    )
  }
]

export const getTripColumns = (
  version: 'FULL' | 'PARTIAL' | 'LIGHT' | 'SUMMARY'
): Column<Trip>[] => {
  // Map the input version to the internal TripVersion
  const columns = tripsColumns()

  return columns.filter((column) => column.version.includes(version))
}

export const mapTripInputValues = (trip: TripInput): TripInput => {
  const mapped: TripInput = {
    type: trip.type,
    hasTransport: trip.hasTransport,
    // keep countries as null when not provided to match form behaviour
    countries: trip.countries ?? null,
    // keep explicit undefined when not provided
    foreignProviderArrivalPlaceId: trip.foreignProviderArrivalPlaceId
  }

  if (trip.hasTransport) {
    mapped.transportMeansId = trip.transportMeansId ?? null
    mapped.arrival = trip.arrival ?? null
    mapped.departure = trip.departure ?? null

    if (trip.linkedToMainTripId !== undefined)
      mapped.linkedToMainTripId = trip.linkedToMainTripId ?? null
  } else {
    // without transport, include journeyPlaceId and ensure transport fields are not sent
    mapped.journeyPlaceId = trip.journeyPlaceId ?? null
  }

  return mapped
}
